<template>
  <AppHeader />

  <div class="container mx-auto p-4">
    <div
      class="max-w-screen-2x1 mx-auto bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden
             text-gray-900 dark:text-gray-100"
    >
      <!-- Header / Filters -->
      <div class="p-4 border-b dark:border-slate-700 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 class="text-xl font-semibold">Registros (Admin)</h1>
          <p class="text-xs text-gray-500 dark:text-gray-400">Visualize todos os lançamentos por unidade</p>
        </div>

        <div class="flex items-center gap-2">
          <select
            v-model="filters.unit"
            :disabled="isManager"
            class="px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-700"
          >
            <template v-if="isManager">
              <option :value="userUnit">{{ userUnit }}</option>
            </template>
            <template v-else>
              <option value="">Todas as unidades</option>
              <option value="GPI">GPI</option>
              <option value="GEOF">GEOF</option>
              <option value="GEAAL">GEAAL</option>
            </template>
            
          </select>

          <input
            v-model="filters.q"
            type="text"
            placeholder="Pesquisar…"
            class="px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-700"
          />

          <input
            v-model="filters.from"
            type="date"
            class="px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-700"
          />
          <input
            v-model="filters.to"
            type="date"
            class="px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-700"
          />

          <button
            @click="resetFilters"
            class="px-3 py-2 rounded-md bg-slate-600 text-white hover:bg-slate-700"
          >
            Limpar Filtro
          </button>

          <button
            @click="load"
            class="px-3 py-2 rounded-md bg-slate-600 text-white hover:bg-slate-700"
          >
            Pesquisar
          </button>
        </div>

        <!-- Date presets + column toggle -->
        <div class="w-full flex items-center gap-2">
          <div class="flex items-center gap-2">
            <button class="px-2 py-1 text-xs rounded-lg border dark:border-slate-600" 
            :class="activePreset === 'today' ? 'bg-emerald-600 text-white border-emerald-600' : ''"
                    @click="setPreset('today')">Hoje</button>
            <button class="px-2 py-1 text-xs rounded-lg border dark:border-slate-600"
            :class="activePreset === 'week' ? 'bg-emerald-600 text-white border-emerald-600' : ''"
                    @click="setPreset('week')">Esta semana</button>
            <button class="px-2 py-1 text-xs rounded-lg border dark:border-slate-600"
            :class="activePreset === 'month' ? 'bg-emerald-600 text-white border-emerald-600' : ''"
                    @click="setPreset('month')">Este mês</button>
          </div>

          <label class="ml-auto text-xs flex items-center gap-1">
            <input type="checkbox" v-model="showNotes" />
            Mostrar observações
          </label>
        </div>
      </div>

      <!-- Stats bar -->
      <div class="px-4 pt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div class="p-3 rounded-lg bg-gray-50 dark:bg-slate-700/50">
          <div class="text-xs text-gray-500 dark:text-gray-300">Registros</div>
          <div class="text-lg font-semibold">{{ rows.length }}</div>
        </div>
        <div class="p-3 rounded-lg bg-gray-50 dark:bg-slate-700/50">
          <div class="text-xs text-gray-500 dark:text-gray-300">Total (horas)</div>
          <div class="text-lg font-semibold">{{ (totalMinutes/60).toFixed(1) }} h</div>
        </div>
        <div class="p-3 rounded-lg bg-gray-50 dark:bg-slate-700/50">
          <div class="text-xs text-gray-500 dark:text-gray-300">Média por registro</div>
          <div class="text-lg font-semibold">{{ avgPerEntry }}</div>
        </div>
        <div class="p-3 rounded-lg bg-gray-50 dark:bg-slate-700/50">
          <div class="text-xs text-gray-500 dark:text-gray-300">Intervalo ativo</div>
          <div class="text-sm">{{ filters.from || '—' }} → {{ filters.to || '—' }}</div>
        </div>
      </div>

      <!-- Grouping / Export -->
      <div class="px-4 pt-2 flex items-center gap-2">
        <button
          v-for="g in ['unit','collaborator','date']"
          :key="g"
          class="px-3 py-1 rounded-lg border text-sm"
          :class="groupBy===g
            ? 'bg-emerald-600 text-white border-emerald-600'
            : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'"
          @click="loadSummary(g)"
        >
          {{ g==='unit' ? 'Por unidade' : g==='collaborator' ? 'Por colaborador' : 'Por data' }}
        </button>

        <button
          @click="exportCsv"
          class="ml-auto px-3 py-1 rounded-lg border text-sm bg-white dark:bg-slate-800 dark:border-slate-600"
        >
          Exportar CSV
        </button>
      </div>

      <!-- Summary table -->
      <div v-if="summary.length" class="px-4 pt-2 overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="text-left bg-gray-50 dark:bg-slate-700/60">
            <tr>
              <th class="px-3 py-2 w-1/2">Grupo</th>
              <th class="px-3 py-2">Registros</th>
              <th class="px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in summary" :key="r.label" class="border-b dark:border-slate-700">
              <td class="px-3 py-2 truncate" :title="r.label">{{ r.label }}</td>
              <td class="px-3 py-2">{{ r.registros }}</td>
              <td class="px-3 py-2">
                {{ Math.floor((r.total_minutos||0)/60) }}h {{ (r.total_minutos||0)%60 }}m
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Detailed table -->
      <div class="p-4 overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="text-left bg-gray-50 dark:bg-slate-700/60">
            <tr>
              <th class="px-3 py-2 cursor-pointer" @click="sortBy('gerencia')">
                Unidade
                <span v-if="sort.key==='gerencia'">({{ sort.dir==='asc'?'▲':'▼' }})</span>
              </th>
              <th class="px-3 py-2">Data de Início</th>
              <th class="px-3 py-2">Data de Conclusão</th>
              <th class="px-3 py-2">Colaborador</th>
              <th class="px-3 py-2">Duração</th>
              <th class="px-3 py-2">Processo</th>
              <th class="px-3 py-2">Entrega</th>
              <th class="px-3 py-2">Tarefa</th>
              <th class="px-3 py-2">Complexidade</th>
              <th class="px-3 py-2">Itens</th>
              <th class="px-3 py-2">Pontos</th>
              <th class="px-3 py-2" v-if="showNotes">Observações</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in sorted"
              :key="r.id"
              class="border-b dark:border-slate-700 last:border-0 hover:bg-gray-50 dark:hover:bg-slate-700/50"
            >
              <td class="px-3 py-2">{{ r.gerencia }}</td>
              <td class="px-3 py-2">{{ formatDate(r.start_date) }}</td>
              <td class="px-3 py-2">{{ formatDate(r.current_date) }}</td>
              <td class="px-3 py-2">{{ r.collaborator }}</td>
              <td class="px-3 py-2">{{ fmtDuration(r.time_duration) }}</td>
              <td class="px-3 py-2">{{ geofLabelMap.map[r.process] || r.process }}</td>
              <td class="px-3 py-2">{{ geofLabelMap.submission[r.dispatch] || r.dispatch }}</td>
              <td class="px-3 py-2">{{ geofLabelMap.map[r.task] || r.task }}</td>
              <td class="px-3 py-2">{{ geofLabelMap.complexity[r.dispatch] || '' }}</td>
              <td class="px-3 py-2">{{ r.items }}</td>
              <td class="px-3 py-2">{{ r.dispatch_points }}</td>
              <td class="px-3 py-2 max-w-xs" :title="r.notes" v-if="showNotes">
                <div class="max-h-20 overflow-y-auto">{{ r.notes }}</div>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td :colspan="showNotes ? 10 : 9" class="px-3 py-6 text-center text-gray-500">
                Nenhum registro encontrado.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="fixed top-4 right-4 z-50" v-if="error">
      <div class="px-4 py-2 rounded-md bg-red-600 text-white shadow">{{ error }}</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppHeader from './Header.vue'
import { processos_GEOF, hierarquia_GEOF, weight_complexity_GEOF } from "./GEOF";

export default {
  name: 'AdminEntries',
  components: { AppHeader },
  data() {
    const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname)
    return {
      apiBase: isLocal ? 'http://localhost:5000' : '/api',

      rows: [],
      error: '',
      loading: false,

      // filters
      filters: { unit: '', q: '', from: '', to: '' },

      // sorting
      sort: { key: 'gerencia', dir: 'asc' },

      // summary/grouping
      groupBy: 'unit',
      summary: [],

      // UI toggles
      showNotes: true,

      // debounce timer (fixed: no underscore)
      filterTimer: null,

      //Tasks, dispatches, processes and complexity's objects
      processos_GEOF,
      hierarquia_GEOF,
      weight_complexity_GEOF,

      //Manager verifier
      isManager: '',
      userUnit: '',
      user: '',

      //Set "month" preset
      activePreset: 'month' 
    }
  },
  computed: {
    sorted() {
      const arr = [...this.rows]
      const k = this.sort.key
      const dir = this.sort.dir === 'asc' ? 1 : -1
      return arr.sort((a, b) => {
        const va = String(a[k] || '').toUpperCase()
        const vb = String(b[k] || '').toUpperCase()
        return va.localeCompare(vb) * dir
      })
    },
    totalMinutes() {
      return this.rows.reduce(
        (s, r) => s + (Number.isFinite(r.time_duration) ? r.time_duration : 0),
        0
      )
    },
    avgPerEntry() {
      if (!this.rows.length) return '0h 0m'
      const avg = Math.round(this.totalMinutes / this.rows.length)
      const h = Math.floor(avg / 60)
      const m = avg % 60
      return `${h}h ${m}m`
    },
    //Mapear as labels de hierarquia_GEOF
    geofLabelMap() {
      const map = {}, submission = {}, complexity = {}

      this.hierarquia_GEOF.forEach(proc => {

        // processo
        map[proc.id] = proc.label

        // entregas
        proc.entrega?.forEach(a => {
          map[a.id] = a.label
          submission[a.id] = a.submission
          complexity[a.id] = a.complexity
        })

        // tarefas
        proc.tarefa?.forEach(e => {
          map[e.id] = e.label
        })

      })

      return { map, submission, complexity}
    },
  },
  created() {
    try {
      const raw = localStorage.getItem('user')
      console.log(raw)
      this.user = raw ? JSON.parse(raw) : {}
      this.userUnit = this.user.unit.toUpperCase()
      if ((this.user.manager == 1 || this.user.manager == true) && (this.user.admin == 0 || this.user.admin == false)) {
        this.isManager = true
      }
      else this.isManager = false
      if (this.isManager) this.filters.unit = this.userUnit
    }catch(e) {
      console.debug('manager preselect unit: parse error', e);
    }
    
    this.setPreset('month')
  },
  methods: {
    async load() {
      this.error = ''
      this.loading = true
      try {
        const params = {}
        if (this.filters.unit) params.unit = this.filters.unit
        if (this.filters.q)    params.q    = this.filters.q
        if (this.filters.from) params.from = this.filters.from
        if (this.filters.to)   params.to   = this.filters.to

        const { data } = await axios.get(`${this.apiBase}/admin/entries`, { params })
        // normaliza a gerencia para MAIÚSCULAS ao receber os dados
        this.rows = Array.isArray(data)
          ? data.map(r => ({ ...r, gerencia: String(r.gerencia || '').toUpperCase() }))
          : []
      } catch (e) {
        console.error(e)
        this.rows = []
        this.error = e?.response?.data?.error || 'Falha ao carregar registros.'
      } finally {
        this.loading = false
        // refresh summary too
        this.loadSummary(this.groupBy)
      }
    },

    async loadSummary(group = 'unit') {
      this.groupBy = group
      try {
        const params = { group_by: group }
        if (this.filters.unit) params.unit = this.filters.unit
        if (this.filters.q)    params.q    = this.filters.q
        if (this.filters.from) params.from = this.filters.from
        if (this.filters.to)   params.to   = this.filters.to

        const { data } = await axios.get(`${this.apiBase}/admin/entries/summary`, { params })

        if (!Array.isArray(data)) {
          this.summary = []
          return
        }

        if (group === 'date') {
          // agrupa por mês/ano (ex: "agosto de 2025") e soma registros/total_minutos
          const map = {}
          const pad = n => String(n).padStart(2, '0')

          data.forEach(s => {
            let dt = new Date(s.label)
            if (isNaN(dt)) {
              // tenta parse de YYYY-MM-DD
              const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s.label))
              if (m) dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
            }

            // chave para agrupar e ordenar
            const key = !isNaN(dt) ? `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}` : String(s.label)
            const label = !isNaN(dt)
              ? dt.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase()
              : String(s.label)

            if (!map[key]) {
              map[key] = { key, label, registros: 0, total_minutos: 0 }
            }
            map[key].registros += Number(s.registros || 0)
            map[key].total_minutos += Number(s.total_minutos || 0)
          })

          // converte para array e ordena por key (asc)
          this.summary = Object.values(map)
            .sort((a, b) => a.key.localeCompare(b.key))
            .map(obj => {
              const copy = { ...obj };
              delete copy.key;
              return copy;
            })
        } else {
          // comportamento anterior (normaliza para MAIÚSCULAS)
          this.summary = data.map(s => ({ ...s, label: String(s.label || '').toUpperCase() }))
        }
      } catch (e) {
        console.error(e)
        this.summary = []
      }
    },

    exportCsv() {
      const p = new URLSearchParams()
      if (this.filters.unit) p.set('unit', this.filters.unit)
      if (this.filters.q)    p.set('q', this.filters.q)
      if (this.filters.from) p.set('from', this.filters.from)
      if (this.filters.to)   p.set('to', this.filters.to)

      this.loading = true
      axios.get(`${this.apiBase}/admin/entries/export.csv?${p.toString()}`, {
        responseType: 'blob'
      })
      .then(res => {
        const blob = new Blob([res.data], { type: res.headers['content-type'] || 'text/csv' })
        // tenta extrair filename do Content-Disposition, se houver
        let filename = 'export.csv'
        const cd = res.headers['content-disposition']
        if (cd) {
          const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/.exec(cd)
          if (m) filename = decodeURIComponent(m[1] || m[2])
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      })
      .catch(err => {
        console.error('Export CSV failed', err)
        this.error = 'Falha ao exportar CSV.'
      })
      .finally(() => { this.loading = false })
    },

    resetFilters() {
      this.activePreset = null
      if (this.isManager) {
        this.filters = { unit: this.userUnit, q: '', from: '', to: '' }
      }
      else this.filters = { unit: '', q: '', from: '', to: '' }
      this.load()
    },
    formatDate(isoString) {
      if (!isoString) return ''
      const parts = isoString.split('T')
      const date = parts[0].replace(/-/g, '/')
      if (parts.length < 2) return date 
      const [h, m] = parts[1].split(':')
      return `${date} - ${h}:${m}`
    },

    setPreset(which) {
      this.activePreset = which
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      const fmt = x => `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`

      if (which === 'today') {
        const s = fmt(d)
        this.filters.from = s
        this.filters.to = s
      } else if (which === 'week') {
        const day = d.getDay() || 7
        const start = new Date(d); start.setDate(d.getDate() - (day - 1))
        const end = new Date(start); end.setDate(start.getDate() + 6)
        this.filters.from = fmt(start)
        this.filters.to = fmt(end)
      } else if (which === 'month') {
        const start = new Date(d.getFullYear(), d.getMonth(), 1)
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 0)
        this.filters.from = fmt(start)
        this.filters.to = fmt(end)
      }
      this.load()
    },

    sortBy(key) {
      if (this.sort.key === key) {
        this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.key = key
        this.sort.dir = 'asc'
      }
    },

    fmtDuration(totalMinutes) {
      const t = Number.isFinite(totalMinutes) ? totalMinutes : 0
      const h = Math.floor(t / 60)
      const m = t % 60
      return `${h}h ${m}m`
    },
  },
}
</script>
